import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { firstFocusable } from '../utils';

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function useThrottle<T>(value: T, interval: number): T {
  const [throttledValue, setThrottledValue] = useState<T>(value);
  const lastUpdated = useRef<number>(Date.now());

  useEffect(() => {
    const now = Date.now();

    if (now >= lastUpdated.current + interval) {
      lastUpdated.current = now;
      setThrottledValue(value);
    } else {
      const id = setTimeout(() => {
        lastUpdated.current = Date.now();
        setThrottledValue(value);
      }, interval);

      return () => clearTimeout(id);
    }
  }, [value, interval]);

  return throttledValue;
}

export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((val: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      try {
        const valueToStore =
          value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      } catch (error) {
        console.error(`Error saving to localStorage key "${key}":`, error);
      }
    },
    [key, storedValue]
  );

  return [storedValue, setValue];
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

export function useOnClickOutside<T extends HTMLElement>(
  handler: () => void
): React.RefObject<T> {
  const ref = useRef<T>(null);

  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return;
      }
      handler();
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [handler]);

  return ref;
}

// Высота элемента с подпиской на её изменения. Без ResizeObserver (jsdom)
// остаётся undefined — потребитель должен работать и без неё.
export function useElementHeight<T extends HTMLElement>(): [React.RefObject<T>, number | undefined] {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      setHeight(Math.floor(entry.contentRect.height));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, height];
}

// Фокус всплывающей панели, которая монтируется при каждом открытии: при
// появлении — в первое поле, при закрытии — обратно на кнопку, которая её
// открыла, чтобы с клавиатуры не терять место на странице.
export function usePopupFocus<T extends HTMLElement>(): React.RefObject<T> {
  const ref = useRef<T>(null);

  useEffect(() => {
    const panel = ref.current;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Всплывашку монтируют до позиционирования: фокус ставим кадром позже и
    // без прокрутки — иначе страница уехала бы к панели, ещё стоящей не на месте.
    const frame = requestAnimationFrame(() => {
      if (panel) firstFocusable(panel)?.focus({ preventScroll: true });
    });
    return () => {
      cancelAnimationFrame(frame);
      // Возвращаем, только если фокус пропал вместе с панелью или остался на
      // обёртке кнопки: кликом мимо панели он ушёл туда, куда кликнули.
      const active = document.activeElement;
      const lost = active == null
        || active === document.body
        || (panel?.contains(active) ?? false)
        || (opener != null && active.contains(opener));
      if (lost && opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);

  return ref;
}

export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

export function useToggle(initialValue = false): [boolean, () => void] {
  const [value, setValue] = useState(initialValue);
  const toggle = useCallback(() => setValue((v) => !v), []);
  return [value, toggle];
}

export function useMount(callback: () => void): void {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(callback, []);
}

export function useUnmount(callback: () => void): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    return () => callbackRef.current();
  }, []);
}

interface WindowSize {
  width: number;
  height: number;
}

export function useWindowSize(): WindowSize {
  const [size, setSize] = useState<WindowSize>({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return size;
}

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const breakpoints: Record<Breakpoint, number> = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

export function useBreakpoint(): Breakpoint {
  const { width } = useWindowSize();

  return useMemo(() => {
    if (width >= breakpoints['2xl']) return '2xl';
    if (width >= breakpoints.xl) return 'xl';
    if (width >= breakpoints.lg) return 'lg';
    if (width >= breakpoints.md) return 'md';
    if (width >= breakpoints.sm) return 'sm';
    return 'xs';
  }, [width]);
}

export function useIsFirstRender(): boolean {
  const isFirstRender = useRef(true);

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  return isFirstRender.current;
}

export function useDocumentTitle(title: string): void {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    return () => {
      document.title = previousTitle;
    };
  }, [title]);
}

export interface CommentEditor {
  editing: boolean;
  draft: string;
  setDraft: (v: string) => void;
  isSaving: boolean;
  startEdit: () => void;
  cancelEdit: () => void;
  save: () => void;
}

// Управляет черновиком редактируемого обоснования. `current` — текущее
// сохранённое значение; `saveComment` получает обрезанный черновик (пустая
// строка = снять) и возвращает признак успеха — на нём решаем, закрывать ли
// редактор. Сообщения/инвалидация — забота вызывающего.
export function useCommentEditor(
  current: string,
  saveComment: (comment: string) => Promise<boolean>,
): CommentEditor {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const startEdit = useCallback(() => {
    setDraft(current);
    setEditing(true);
  }, [current]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setDraft('');
  }, []);

  const save = useCallback(async () => {
    const next = draft.trim();
    if (next === current) {
      cancelEdit();
      return;
    }
    setIsSaving(true);
    const ok = await saveComment(next);
    setIsSaving(false);
    if (ok) {
      setEditing(false);
      setDraft('');
    }
  }, [draft, current, saveComment, cancelEdit]);

  return { editing, draft, setDraft, isSaving, startEdit, cancelEdit, save };
}
export { useBeforeUnload } from './useBeforeUnload';
